import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../App";
import Sidebar from "../components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import RichTextEditor from "../components/RichTextEditor";
import { QRCodeCanvas } from "qrcode.react";
import {
  Monitor, Plus, Trash2, Edit, Copy, Link, Play, Image, Type, Video, Globe,
  GripVertical, ChevronUp, ChevronDown, ExternalLink, RefreshCw, Eye, ListOrdered, Upload, Loader2, QrCode, Download, Wifi, WifiOff
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function SignageProgramadorPage() {
  const { getAuthHeaders } = useAuth();
  const [canales, setCanales] = useState([]);
  const [selectedCanal, setSelectedCanal] = useState(null);
  const [contenido, setContenido] = useState([]);
  const [showCanalDialog, setShowCanalDialog] = useState(false);
  const [showContentDialog, setShowContentDialog] = useState(false);
  const [editingCanal, setEditingCanal] = useState(null);
  const [editingContent, setEditingContent] = useState(null);
  const [canalForm, setCanalForm] = useState({ nombre: "", descripcion: "", slug: "", auto_fullscreen: true, skip_start_screen: false });
  const [contentForm, setContentForm] = useState({ tipo: "video", titulo: "", url: "", texto: "", duracion: 10, config: {} });
  const [loading, setLoading] = useState(false);
  const [showPlayOption, setShowPlayOption] = useState(false);
  const [pendingContent, setPendingContent] = useState(null);
  const [showLivePreview, setShowLivePreview] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imgDragOver, setImgDragOver] = useState(false);
  const [qrCanal, setQrCanal] = useState(null);
  const [heartbeats, setHeartbeats] = useState({ total: 0, online: 0, offline: 0, displays: [] });
  const [showHeartbeats, setShowHeartbeats] = useState(false);

  const fetchCanales = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/signage/canales`, getAuthHeaders());
      setCanales(res.data);
    } catch (err) { console.error(err); }
  }, [getAuthHeaders]);

  const fetchContenido = useCallback(async (canalId) => {
    try {
      const res = await axios.get(`${API_URL}/api/signage/canales/${canalId}/contenido`, getAuthHeaders());
      setContenido(res.data);
    } catch (err) { console.error(err); }
  }, [getAuthHeaders]);

  const fetchHeartbeats = useCallback(async (canalId) => {
    try {
      const res = await axios.get(`${API_URL}/api/signage/canales/${canalId}/heartbeats`, getAuthHeaders());
      setHeartbeats(res.data);
    } catch (err) {
      setHeartbeats({ total: 0, online: 0, offline: 0, displays: [] });
    }
  }, [getAuthHeaders]);

  useEffect(() => { fetchCanales(); }, [fetchCanales]);
  useEffect(() => {
    if (selectedCanal) {
      fetchContenido(selectedCanal.id);
      fetchHeartbeats(selectedCanal.id);
    }
  }, [selectedCanal, fetchContenido, fetchHeartbeats]);

  // Polling de heartbeats cada 30s cuando hay canal seleccionado
  useEffect(() => {
    if (!selectedCanal) return;
    const interval = setInterval(() => fetchHeartbeats(selectedCanal.id), 30000);
    return () => clearInterval(interval);
  }, [selectedCanal, fetchHeartbeats]);

  const removeDisplay = async (displayId) => {
    if (!selectedCanal) return;
    if (!window.confirm("¿Remover este display del monitoreo? Volverá a aparecer si vuelve a conectarse.")) return;
    try {
      await axios.delete(`${API_URL}/api/signage/canales/${selectedCanal.id}/heartbeats/${displayId}`, getAuthHeaders());
      toast.success("Display removido");
      fetchHeartbeats(selectedCanal.id);
    } catch (err) {
      toast.error("Error al remover display");
    }
  };

  const formatRelativeTime = (iso) => {
    if (!iso) return "—";
    try {
      const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
      if (diff < 60) return `hace ${diff}s`;
      if (diff < 3600) return `hace ${Math.floor(diff / 60)}min`;
      if (diff < 86400) return `hace ${Math.floor(diff / 3600)}h`;
      return `hace ${Math.floor(diff / 86400)}d`;
    } catch (e) { return iso; }
  };

  const handleSaveCanal = async () => {
    try {
      if (editingCanal) {
        await axios.put(`${API_URL}/api/signage/canales/${editingCanal.id}`, canalForm, getAuthHeaders());
        toast.success("Canal actualizado");
      } else {
        const res = await axios.post(`${API_URL}/api/signage/canales`, canalForm, getAuthHeaders());
        toast.success("Canal creado");
        setSelectedCanal(res.data);
      }
      setShowCanalDialog(false);
      fetchCanales();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const handleDeleteCanal = async (id) => {
    if (!window.confirm("Eliminar este canal y todo su contenido?")) return;
    try {
      await axios.delete(`${API_URL}/api/signage/canales/${id}`, getAuthHeaders());
      toast.success("Canal eliminado");
      if (selectedCanal?.id === id) { setSelectedCanal(null); setContenido([]); }
      fetchCanales();
    } catch (err) { toast.error("Error al eliminar"); }
  };

  const handleSaveContent = async () => {
    if (!selectedCanal) return;
    try {
      if (editingContent) {
        await axios.put(`${API_URL}/api/signage/canales/${selectedCanal.id}/contenido/${editingContent.id}`, contentForm, getAuthHeaders());
        toast.success("Contenido actualizado");
        setShowContentDialog(false);
        fetchContenido(selectedCanal.id);
      } else {
        // New content — ask where to place it
        const res = await axios.post(`${API_URL}/api/signage/canales/${selectedCanal.id}/contenido`, contentForm, getAuthHeaders());
        setPendingContent(res.data);
        setShowContentDialog(false);
        setShowPlayOption(true);
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const handlePlayOption = async (option) => {
    if (!selectedCanal || !pendingContent) return;
    try {
      if (option === "now") {
        // Move to position 0 (first)
        const currentItems = [...contenido.filter(c => c.id !== pendingContent.id)];
        const newOrder = [pendingContent.id, ...currentItems.map(c => c.id)];
        await axios.post(`${API_URL}/api/signage/canales/${selectedCanal.id}/contenido/reorder`, { item_ids: newOrder }, getAuthHeaders());
        // Signal display to reset to index 0
        await axios.put(`${API_URL}/api/signage/canales/${selectedCanal.id}`, { descripcion: selectedCanal.descripcion }, getAuthHeaders());
        toast.success("Reproduciéndose ahora (primero en la cola)");
      } else if (option === "next") {
        // Place at position 1 (after current)
        const currentItems = contenido.filter(c => c.id !== pendingContent.id);
        const newOrder = currentItems.length > 0
          ? [currentItems[0].id, pendingContent.id, ...currentItems.slice(1).map(c => c.id)]
          : [pendingContent.id];
        await axios.post(`${API_URL}/api/signage/canales/${selectedCanal.id}/contenido/reorder`, { item_ids: newOrder }, getAuthHeaders());
        toast.success("Se reproducirá después del contenido actual");
      } else {
        // "last" — already at the end by default
        toast.success("Agregado al final de la cola");
      }
    } catch (err) { toast.error("Error al reordenar"); }
    setShowPlayOption(false);
    setPendingContent(null);
    fetchContenido(selectedCanal.id);
  };

  const handleDeleteContent = async (itemId) => {
    if (!selectedCanal) return;
    try {
      await axios.delete(`${API_URL}/api/signage/canales/${selectedCanal.id}/contenido/${itemId}`, getAuthHeaders());
      toast.success("Eliminado");
      fetchContenido(selectedCanal.id);
    } catch (err) { toast.error("Error"); }
  };

  const handleToggleContent = async (item) => {
    try {
      await axios.put(`${API_URL}/api/signage/canales/${selectedCanal.id}/contenido/${item.id}`, { activo: !item.activo }, getAuthHeaders());
      fetchContenido(selectedCanal.id);
    } catch (err) { toast.error("Error"); }
  };

  const handleMoveContent = async (index, direction) => {
    const newList = [...contenido];
    const swapIdx = index + direction;
    if (swapIdx < 0 || swapIdx >= newList.length) return;
    [newList[index], newList[swapIdx]] = [newList[swapIdx], newList[index]];
    setContenido(newList);
    try {
      await axios.post(`${API_URL}/api/signage/canales/${selectedCanal.id}/contenido/reorder`, { item_ids: newList.map(i => i.id) }, getAuthHeaders());
    } catch (err) { toast.error("Error al reordenar"); }
  };

  const getCanalPublicPath = (canal) => `/display/${canal.slug || canal.token}`;
  const getCanalPublicUrl = (canal) => `${window.location.origin}${getCanalPublicPath(canal)}`;

  const copyDisplayUrl = (canal) => {
    const url = getCanalPublicUrl(canal);
    navigator.clipboard.writeText(url);
    toast.success("Link copiado al portapapeles");
  };

  const downloadQrCode = (canal) => {
    const canvas = document.getElementById(`qr-canvas-${canal.id}`);
    if (!canvas) { toast.error("No se pudo generar el QR"); return; }
    const pngUrl = canvas.toDataURL("image/png");
    const link = document.createElement("a");
    link.href = pngUrl;
    const safeName = (canal.slug || canal.nombre || "canal").replace(/[^a-z0-9-]/gi, "-").toLowerCase();
    link.download = `qr-${safeName}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("QR descargado");
  };

  const openNewContent = () => {
    setEditingContent(null);
    setContentForm({ tipo: "video", titulo: "", url: "", texto: "", duracion: 10, config: {} });
    setShowContentDialog(true);
  };

  const openEditContent = (item) => {
    setEditingContent(item);
    setContentForm({ tipo: item.tipo, titulo: item.titulo, url: item.url, texto: item.texto, duracion: item.duracion, config: item.config || {} });
    setShowContentDialog(true);
  };

  const tipoIcon = (tipo) => {
    switch (tipo) {
      case "video": return <Video className="w-4 h-4 text-red-500" />;
      case "imagen": return <Image className="w-4 h-4 text-green-500" />;
      case "texto": return <Type className="w-4 h-4 text-blue-500" />;
      case "link": return <Globe className="w-4 h-4 text-purple-500" />;
      case "bienvenida": return <Type className="w-4 h-4 text-amber-500" />;
      case "sala": return <Monitor className="w-4 h-4 text-cyan-500" />;
      default: return <Monitor className="w-4 h-4" />;
    }
  };

  const handleToggleSala = async (item) => {
    if (!selectedCanal || item.tipo !== "sala") return;
    try {
      const res = await axios.put(`${API_URL}/api/signage/canales/${selectedCanal.id}/contenido/${item.id}/sala-status`, {}, getAuthHeaders());
      toast.success(res.data.ocupada ? "Sala marcada como OCUPADA" : "Sala marcada como DISPONIBLE");
      fetchContenido(selectedCanal.id);
    } catch (err) { toast.error("Error"); }
  };

  const handleUploadImage = async (file) => {
    return handleUploadFile(file, "imagen");
  };

  const handleUploadFile = async (file, tipo) => {
    if (!selectedCanal || !file) return;
    const isVideo = tipo === "video";
    const validImageTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/gif"];
    const validVideoTypes = ["video/mp4", "video/webm", "video/ogg", "video/quicktime"];
    const validTypes = isVideo ? validVideoTypes : validImageTypes;
    if (!validTypes.includes(file.type)) {
      toast.error(isVideo ? "Solo MP4, WEBM" : "Solo JPEG, PNG, WEBP, GIF");
      return;
    }
    const maxSize = isVideo ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(`Archivo muy grande (máx ${maxSize / (1024*1024)}MB)`);
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const headers = getAuthHeaders().headers || {};
      const res = await fetch(`${API_URL}/api/signage/canales/${selectedCanal.id}/upload-imagen`, {
        method: "POST",
        headers: { Authorization: headers.Authorization || headers.authorization },
        body: formData
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Error al subir archivo");
      }
      const data = await res.json();
      const newUrl = `${API_URL}${data.url}`;
      if (isVideo) {
        setContentForm(prev => ({ ...prev, url: newUrl, titulo: prev.titulo || file.name.replace(/\.[^.]+$/, ""), config: { ...prev.config, uploadedVideo: file.name } }));
      } else {
        setContentForm(prev => ({ ...prev, url: newUrl, titulo: prev.titulo || file.name.replace(/\.[^.]+$/, "") }));
      }
      toast.success(`${isVideo ? "Video" : "Imagen"} subido correctamente`);
    } catch (err) {
      toast.error(err.message || "Error al subir");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <div className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Cartelera Digital</h1>
            <p className="text-sm text-slate-500">Programa contenido para pantallas y TVs</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Panel izquierdo: Canales */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Canales / Pantallas</CardTitle>
                  <Button size="sm" onClick={() => { setEditingCanal(null); setCanalForm({ nombre: "", descripcion: "", slug: "", auto_fullscreen: true, skip_start_screen: false }); setShowCanalDialog(true); }}>
                    <Plus className="w-4 h-4 mr-1" /> Nuevo
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-2 max-h-[70vh] overflow-y-auto">
                {canales.length === 0 && <p className="text-sm text-slate-400 text-center py-8">No hay canales creados</p>}
                {canales.map(canal => (
                  <div
                    key={canal.id}
                    className={`p-3 rounded-lg border cursor-pointer transition-all ${selectedCanal?.id === canal.id ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20" : "border-slate-200 hover:border-slate-300 dark:border-slate-700"}`}
                    onClick={() => setSelectedCanal(canal)}
                    data-testid={`canal-${canal.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Monitor className={`w-5 h-5 ${canal.activo ? "text-green-500" : "text-slate-400"}`} />
                        <div>
                          <p className="text-sm font-medium">{canal.nombre}</p>
                          <p className="text-[11px] text-slate-500">{canal.contenido_count || 0} items</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={(e) => { e.stopPropagation(); copyDisplayUrl(canal); }} className="p-1 rounded hover:bg-slate-200" title="Copiar link">
                          <Link className="w-3.5 h-3.5 text-slate-500" />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); setQrCanal(canal); }} className="p-1 rounded hover:bg-slate-200" title="Ver código QR" data-testid={`canal-qr-btn-${canal.id}`}>
                          <QrCode className="w-3.5 h-3.5 text-purple-500" />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); window.open(getCanalPublicPath(canal), '_blank'); }} className="p-1 rounded hover:bg-slate-200" title="Ver display">
                          <Eye className="w-3.5 h-3.5 text-blue-500" />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); setEditingCanal(canal); setCanalForm({ nombre: canal.nombre, descripcion: canal.descripcion || "", slug: canal.slug || "", auto_fullscreen: canal.auto_fullscreen !== false, skip_start_screen: !!canal.skip_start_screen }); setShowCanalDialog(true); }} className="p-1 rounded hover:bg-slate-200" title="Editar">
                          <Edit className="w-3.5 h-3.5 text-slate-500" />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); handleDeleteCanal(canal.id); }} className="p-1 rounded hover:bg-red-100" title="Eliminar">
                          <Trash2 className="w-3.5 h-3.5 text-red-500" />
                        </button>
                      </div>
                    </div>
                    {selectedCanal?.id === canal.id && (
                      <div className="mt-2 pt-2 border-t border-blue-200 dark:border-blue-700">
                        <div className="flex items-center gap-1 text-[10px] text-slate-500 bg-slate-100 dark:bg-slate-800 rounded px-2 py-1">
                          <Link className="w-3 h-3" />
                          <span className="truncate">{window.location.origin}{getCanalPublicPath(canal)}</span>
                          <button onClick={(e) => { e.stopPropagation(); copyDisplayUrl(canal); }}>
                            <Copy className="w-3 h-3 text-blue-500" />
                          </button>
                        </div>
                        {canal.slug && canal.token && (
                          <div className="text-[9px] text-slate-400 mt-1 truncate">
                            URL alterna (token): /display/{canal.token}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Panel derecho: Contenido del canal */}
          <div className="lg:col-span-2">
            {!selectedCanal ? (
              <Card className="flex items-center justify-center h-96">
                <div className="text-center text-slate-400">
                  <Monitor className="w-16 h-16 mx-auto mb-3 opacity-30" />
                  <p className="text-lg font-medium">Selecciona un canal</p>
                  <p className="text-sm">o crea uno nuevo para comenzar</p>
                </div>
              </Card>
            ) : (
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-base">{selectedCanal.nombre}</CardTitle>
                      <p className="text-xs text-slate-500">{selectedCanal.descripcion}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Indicador de TVs conectadas (heartbeats) */}
                      <button
                        onClick={() => setShowHeartbeats(!showHeartbeats)}
                        className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${heartbeats.online > 0 ? "bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400" : "bg-slate-100 text-slate-500 hover:bg-slate-200 dark:bg-slate-800"}`}
                        title={`${heartbeats.online} TV(s) online · ${heartbeats.offline} offline`}
                        data-testid="heartbeats-toggle-btn"
                      >
                        {heartbeats.online > 0 ? (
                          <><Wifi className="w-3 h-3" /><div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" /></>
                        ) : (
                          <WifiOff className="w-3 h-3" />
                        )}
                        <span>{heartbeats.online}/{heartbeats.total} TVs</span>
                      </button>
                      <Button size="sm" variant={showLivePreview ? "default" : "outline"} onClick={() => setShowLivePreview(!showLivePreview)} className={showLivePreview ? "bg-red-600 hover:bg-red-700" : ""}>
                        <div className={`w-2 h-2 rounded-full mr-1.5 ${showLivePreview ? "bg-white animate-pulse" : "bg-red-500"}`} />
                        {showLivePreview ? "Ocultar" : "En Vivo"}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => window.open(getCanalPublicPath(selectedCanal), '_blank')}>
                        <ExternalLink className="w-4 h-4 mr-1" /> Abrir Display
                      </Button>
                      <Button size="sm" onClick={openNewContent}>
                        <Plus className="w-4 h-4 mr-1" /> Agregar
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                {/* Live Preview Panel */}
                {showLivePreview && selectedCanal && (
                  <div className="mx-4 mb-3 rounded-lg overflow-hidden border-2 border-red-400 relative" style={{ aspectRatio: "16/9" }}>
                    <iframe
                      src={`${getCanalPublicPath(selectedCanal)}?embedded=1`}
                      className="w-full h-full border-0"
                      title="Vista en vivo"
                      allow="autoplay"
                    />
                    <div className="absolute top-2 left-2 flex items-center gap-1.5 bg-red-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                      <div className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                      EN VIVO
                    </div>
                    <button
                      onClick={() => setShowLivePreview(false)}
                      className="absolute top-2 right-2 w-6 h-6 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center"
                    >
                      <span className="text-xs">X</span>
                    </button>
                  </div>
                )}
                {/* Panel de TVs conectadas (heartbeats) */}
                {showHeartbeats && (
                  <div className="mx-4 mb-3 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                    <div className="flex items-center justify-between px-3 py-2 bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                      <div className="flex items-center gap-2">
                        <Monitor className="w-3.5 h-3.5 text-slate-600" />
                        <span className="text-xs font-semibold">Displays conectados</span>
                        <span className="text-[10px] text-slate-500">· reportan cada 60s</span>
                      </div>
                      <button
                        onClick={() => fetchHeartbeats(selectedCanal.id)}
                        className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700"
                        title="Actualizar"
                      >
                        <RefreshCw className="w-3 h-3" />
                      </button>
                    </div>
                    {heartbeats.displays.length === 0 ? (
                      <div className="text-center py-6 text-slate-400 text-xs">
                        <WifiOff className="w-8 h-8 mx-auto mb-1 opacity-40" />
                        <p>No hay TVs conectadas todavía</p>
                        <p className="text-[10px] mt-1">Abre la URL del display en una TV y aparecerá aquí en ~1 minuto</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-slate-100 dark:divide-slate-700 max-h-60 overflow-auto">
                        {heartbeats.displays.map((hb) => (
                          <div key={hb.display_id} className="flex items-center gap-2 px-3 py-2 text-xs hover:bg-slate-50 dark:hover:bg-slate-800/50" data-testid={`display-row-${hb.display_id}`}>
                            <div className="flex items-center gap-1.5 shrink-0">
                              {hb.online ? (
                                <>
                                  <div className="w-2 h-2 rounded-full bg-green-500 shadow-[0_0_6px_rgba(34,197,94,0.6)] animate-pulse" />
                                  <span className="font-semibold text-green-700 dark:text-green-400 text-[10px]">ONLINE</span>
                                </>
                              ) : (
                                <>
                                  <div className="w-2 h-2 rounded-full bg-red-400" />
                                  <span className="font-semibold text-red-500 text-[10px]">OFFLINE</span>
                                </>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 truncate">
                                <span className="font-mono text-[11px] text-slate-700 dark:text-slate-300 truncate" title={hb.display_id}>
                                  {hb.display_id}
                                </span>
                                {hb.paused && <span className="bg-amber-100 text-amber-700 px-1 rounded text-[9px] font-bold">PAUSADO</span>}
                              </div>
                              <div className="flex items-center gap-2 text-[10px] text-slate-500 mt-0.5 truncate">
                                <span title="IP">{hb.ip || "—"}</span>
                                <span>·</span>
                                <span title="Resolución">{hb.resolution || "—"}</span>
                                {hb.orientation && <><span>·</span><span title="Orientación">{hb.orientation}</span></>}
                                <span>·</span>
                                <span title={hb.last_seen}>{formatRelativeTime(hb.last_seen)}</span>
                              </div>
                              {hb.current_item_titulo && (
                                <div className="text-[10px] text-slate-500 mt-0.5 truncate">
                                  <Play className="w-2.5 h-2.5 inline mr-1" />
                                  {hb.current_item_titulo}
                                </div>
                              )}
                            </div>
                            <button
                              onClick={() => removeDisplay(hb.display_id)}
                              className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30"
                              title="Quitar del monitoreo"
                              data-testid={`remove-display-btn-${hb.display_id}`}
                            >
                              <Trash2 className="w-3 h-3 text-red-500" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <CardContent>
                  {contenido.length === 0 ? (
                    <div className="text-center py-12 text-slate-400">
                      <Play className="w-12 h-12 mx-auto mb-2 opacity-30" />
                      <p>No hay contenido en este canal</p>
                      <Button size="sm" className="mt-3" onClick={openNewContent}><Plus className="w-4 h-4 mr-1" /> Agregar contenido</Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {contenido.map((item, index) => (
                        <div key={item.id} className={`flex items-center gap-3 p-3 rounded-lg border ${item.activo ? "border-slate-200 bg-white dark:bg-slate-800" : "border-slate-100 bg-slate-50 opacity-50"}`}>
                          <div className="flex flex-col gap-0.5">
                            <button onClick={() => handleMoveContent(index, -1)} disabled={index === 0} className="p-0.5 rounded hover:bg-slate-200 disabled:opacity-20">
                              <ChevronUp className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => handleMoveContent(index, 1)} disabled={index === contenido.length - 1} className="p-0.5 rounded hover:bg-slate-200 disabled:opacity-20">
                              <ChevronDown className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          <div className="w-8 h-8 rounded-md bg-slate-100 dark:bg-slate-700 flex items-center justify-center shrink-0">
                            {tipoIcon(item.tipo)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{item.titulo || item.url || item.texto?.slice(0, 50) || "Sin título"}</p>
                            <div className="flex items-center gap-2 text-[11px] text-slate-500">
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0">{item.tipo}</Badge>
                              <span>{item.duracion}s</span>
                              {item.config?.horario_inicio && <span className="text-amber-600">{item.config.horario_inicio}-{item.config.horario_fin || "∞"}</span>}
                              {item.tipo === "sala" && <Badge className={item.config?.ocupada ? "bg-red-100 text-red-700 text-[10px]" : "bg-green-100 text-green-700 text-[10px]"}>{item.config?.ocupada ? "Ocupada" : "Disponible"}</Badge>}
                              {item.url && <span className="truncate max-w-[200px]">{item.url}</span>}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            {item.tipo === "sala" && (
                              <button onClick={() => handleToggleSala(item)} className={`px-2 py-0.5 rounded text-[10px] font-medium ${item.config?.ocupada ? "bg-green-100 text-green-700 hover:bg-green-200" : "bg-red-100 text-red-700 hover:bg-red-200"}`}>
                                {item.config?.ocupada ? "Liberar" : "Ocupar"}
                              </button>
                            )}
                            <Switch checked={item.activo} onCheckedChange={() => handleToggleContent(item)} />
                            <button onClick={() => openEditContent(item)} className="p-1.5 rounded hover:bg-slate-200"><Edit className="w-3.5 h-3.5 text-slate-500" /></button>
                            <button onClick={() => handleDeleteContent(item.id)} className="p-1.5 rounded hover:bg-red-100"><Trash2 className="w-3.5 h-3.5 text-red-500" /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Dialog: Canal */}
        <Dialog open={showCanalDialog} onOpenChange={setShowCanalDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle>{editingCanal ? "Editar Canal" : "Nuevo Canal"}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Nombre *</Label>
                <Input
                  value={canalForm.nombre}
                  onChange={e => setCanalForm({ ...canalForm, nombre: e.target.value })}
                  placeholder="Ej: Recepción Principal"
                  data-testid="canal-nombre-input"
                />
              </div>
              <div>
                <Label>Descripción</Label>
                <Input
                  value={canalForm.descripcion}
                  onChange={e => setCanalForm({ ...canalForm, descripcion: e.target.value })}
                  placeholder="Ej: TV de la recepción"
                  data-testid="canal-descripcion-input"
                />
              </div>
              <div>
                <Label>URL personalizada (opcional)</Label>
                <div className="flex items-center gap-1">
                  <span className="text-xs text-slate-400 whitespace-nowrap">/display/</span>
                  <Input
                    value={canalForm.slug || ""}
                    onChange={e => setCanalForm({ ...canalForm, slug: e.target.value.toLowerCase().replace(/[\s_]+/g, "-").replace(/[^a-z0-9-]/g, "") })}
                    placeholder="conferencia"
                    className="flex-1"
                    data-testid="canal-slug-input"
                    maxLength={50}
                  />
                </div>
                <p className="text-[10px] text-slate-500 mt-1">
                  Opcional. Letras minúsculas, números y guiones (3–50 caracteres). Vacío = usar token aleatorio.
                </p>
                {canalForm.slug && (
                  <div className="mt-2 text-[11px] bg-blue-50 dark:bg-blue-900/20 border border-blue-200 rounded px-2 py-1.5 text-blue-700 dark:text-blue-300 break-all">
                    <strong>URL final:</strong> {window.location.origin}/display/{canalForm.slug}
                  </div>
                )}
              </div>
              {/* Modo Kiosko */}
              <div className="border-t border-slate-200 dark:border-slate-700 pt-3">
                <div className="flex items-center gap-2 mb-2">
                  <Monitor className="w-4 h-4 text-purple-600" />
                  <Label className="text-sm font-semibold">Modo Kiosko (para TVs)</Label>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between bg-slate-50 dark:bg-slate-800 rounded px-3 py-2">
                    <div className="flex-1 pr-2">
                      <p className="text-xs font-medium">Pantalla completa automática</p>
                      <p className="text-[10px] text-slate-500">Al primer toque/clic entra en fullscreen sin necesidad del botón inicial</p>
                    </div>
                    <Switch
                      checked={canalForm.auto_fullscreen !== false}
                      onCheckedChange={(v) => setCanalForm({ ...canalForm, auto_fullscreen: v })}
                      data-testid="canal-auto-fullscreen-switch"
                    />
                  </div>
                  <div className="flex items-center justify-between bg-slate-50 dark:bg-slate-800 rounded px-3 py-2">
                    <div className="flex-1 pr-2">
                      <p className="text-xs font-medium">Saltar pantalla de inicio</p>
                      <p className="text-[10px] text-slate-500">Reproduce el contenido inmediatamente sin mostrar "Toca para iniciar". Ideal para TVs con Chromebox/Raspberry Pi en modo kiosko.</p>
                    </div>
                    <Switch
                      checked={!!canalForm.skip_start_screen}
                      onCheckedChange={(v) => setCanalForm({ ...canalForm, skip_start_screen: v })}
                      data-testid="canal-skip-start-switch"
                    />
                  </div>
                </div>
                <p className="text-[10px] text-slate-500 mt-2 italic">
                  💡 Teclas en el display: <strong>F</strong> fullscreen, <strong>Espacio</strong> pausar, <strong>← →</strong> navegar, <strong>Esc</strong> salir.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCanalDialog(false)}>Cancelar</Button>
              <Button onClick={handleSaveCanal} disabled={!canalForm.nombre.trim()}>Guardar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog: Contenido */}
        <Dialog open={showContentDialog} onOpenChange={setShowContentDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader><DialogTitle>{editingContent ? "Editar Contenido" : "Agregar Contenido"}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Tipo de contenido</Label>
                <Select value={contentForm.tipo} onValueChange={v => setContentForm({ ...contentForm, tipo: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="video">Video</SelectItem>
                    <SelectItem value="imagen">Imagen</SelectItem>
                    <SelectItem value="texto">Mensaje de texto</SelectItem>
                    <SelectItem value="bienvenida">Bienvenida a cliente</SelectItem>
                    <SelectItem value="sala">Estado de sala</SelectItem>
                    <SelectItem value="link">Página web (Link)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Título *</Label><Input value={contentForm.titulo} onChange={e => setContentForm({ ...contentForm, titulo: e.target.value })} placeholder={contentForm.tipo === "bienvenida" ? "Ej: Sr. Juan Martínez" : contentForm.tipo === "sala" ? "Ej: Sala de Conferencias A" : "Nombre descriptivo"} /></div>
              {contentForm.tipo === "video" && (
                <div className="space-y-2">
                  <Label>Video *</Label>
                  <div
                    onDragOver={(e) => { e.preventDefault(); setImgDragOver(true); }}
                    onDragLeave={() => setImgDragOver(false)}
                    onDrop={(e) => { e.preventDefault(); setImgDragOver(false); const f = e.dataTransfer?.files?.[0]; if (f) handleUploadFile(f, "video"); }}
                    onClick={() => !uploading && document.getElementById("signage-video-upload")?.click()}
                    className={`border-2 border-dashed rounded-lg p-4 text-center transition-all cursor-pointer ${imgDragOver ? "border-red-500 bg-red-50" : "border-slate-300 hover:border-red-400 bg-slate-50/50"} ${uploading ? "pointer-events-none opacity-60" : ""}`}
                  >
                    <input id="signage-video-upload" type="file" accept="video/mp4,video/webm,video/ogg" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUploadFile(f, "video"); e.target.value = ""; }} disabled={uploading} />
                    {uploading ? (
                      <div className="flex flex-col items-center gap-2 py-2">
                        <Loader2 className="w-6 h-6 text-red-500 animate-spin" />
                        <p className="text-xs text-red-600">Subiendo video...</p>
                      </div>
                    ) : contentForm.config?.uploadedVideo ? (
                      <div className="flex items-center gap-3">
                        <Video className="w-10 h-10 text-red-500 shrink-0" />
                        <div className="text-left flex-1 min-w-0">
                          <p className="text-xs font-medium text-green-700">Video cargado</p>
                          <p className="text-[10px] text-slate-500 truncate">{contentForm.config.uploadedVideo}</p>
                          <button type="button" onClick={(e) => { e.stopPropagation(); setContentForm(prev => ({ ...prev, url: "", config: { ...prev.config, uploadedVideo: null } })); }} className="text-[10px] text-red-500 hover:underline mt-0.5">Cambiar video</button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-1 py-2">
                        <Upload className="w-6 h-6 text-slate-400" />
                        <p className="text-xs text-slate-600">Arrastra un video aquí o haz clic para seleccionar</p>
                        <p className="text-[10px] text-slate-400">MP4, WEBM (máx 50MB)</p>
                      </div>
                    )}
                  </div>
                  <div><Label className="text-[11px] text-slate-500">O pega un link de YouTube/URL:</Label><Input value={contentForm.config?.uploadedVideo ? "" : contentForm.url} onChange={e => setContentForm({ ...contentForm, url: e.target.value, config: { ...contentForm.config, uploadedVideo: null } })} placeholder="https://youtube.com/watch?v=... o URL directa" className="h-7 text-xs" disabled={!!contentForm.config?.uploadedVideo} /></div>
                </div>
              )}
              {contentForm.tipo === "link" && (
                <div><Label>URL *</Label><Input value={contentForm.url} onChange={e => setContentForm({ ...contentForm, url: e.target.value })} placeholder="https://ejemplo.com" /></div>
              )}
              {contentForm.tipo === "imagen" && (
                <div className="space-y-2">
                  <Label>Imagen *</Label>
                  <div
                    onDragOver={(e) => { e.preventDefault(); setImgDragOver(true); }}
                    onDragLeave={() => setImgDragOver(false)}
                    onDrop={(e) => { e.preventDefault(); setImgDragOver(false); const f = e.dataTransfer?.files?.[0]; if (f) handleUploadImage(f); }}
                    onClick={() => !uploading && document.getElementById("signage-img-upload")?.click()}
                    className={`border-2 border-dashed rounded-lg p-4 text-center transition-all cursor-pointer ${imgDragOver ? "border-blue-500 bg-blue-50" : "border-slate-300 hover:border-blue-400 bg-slate-50/50"} ${uploading ? "pointer-events-none opacity-60" : ""}`}
                  >
                    <input id="signage-img-upload" type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUploadImage(f); e.target.value = ""; }} disabled={uploading} />
                    {uploading ? (
                      <div className="flex flex-col items-center gap-2 py-2">
                        <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
                        <p className="text-xs text-blue-600">Subiendo imagen...</p>
                      </div>
                    ) : contentForm.url ? (
                      <div className="flex items-center gap-3">
                        <img src={contentForm.url} alt="Preview" className="w-16 h-16 object-cover rounded" />
                        <div className="text-left flex-1 min-w-0">
                          <p className="text-xs font-medium text-green-700">Imagen cargada</p>
                          <p className="text-[10px] text-slate-500 truncate">{contentForm.url}</p>
                          <button type="button" onClick={(e) => { e.stopPropagation(); setContentForm({ ...contentForm, url: "" }); }} className="text-[10px] text-red-500 hover:underline mt-0.5">Cambiar imagen</button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-1 py-2">
                        <Upload className="w-6 h-6 text-slate-400" />
                        <p className="text-xs text-slate-600">Arrastra una imagen aquí o haz clic para seleccionar</p>
                        <p className="text-[10px] text-slate-400">JPEG, PNG, WEBP, GIF (máx 10MB)</p>
                      </div>
                    )}
                  </div>
                  <div><Label className="text-[11px] text-slate-500">O pega una URL directamente:</Label><Input value={contentForm.url} onChange={e => setContentForm({ ...contentForm, url: e.target.value })} placeholder="https://ejemplo.com/imagen.jpg" className="h-7 text-xs" /></div>
                </div>
              )}
              {(contentForm.tipo === "texto" || contentForm.tipo === "bienvenida") && (
                <div>
                  <Label>{contentForm.tipo === "bienvenida" ? "Mensaje de bienvenida" : "Mensaje *"}</Label>
                  <RichTextEditor 
                    value={contentForm.texto} 
                    onChange={(val) => setContentForm({ ...contentForm, texto: val })} 
                    placeholder={contentForm.tipo === "bienvenida" ? "Bienvenido a nuestra empresa" : "Escriba el mensaje..."} 
                  />
                </div>
              )}
              {contentForm.tipo === "bienvenida" && (
                <div className="grid grid-cols-3 gap-2">
                  <div><Label className="text-xs">Subtítulo</Label><Input value={contentForm.config?.subtitulo || ""} onChange={e => setContentForm({ ...contentForm, config: { ...contentForm.config, subtitulo: e.target.value } })} placeholder="Le damos la bienvenida" className="h-8 text-xs" /></div>
                  <div><Label className="text-xs">Empresa</Label><Input value={contentForm.config?.empresa || ""} onChange={e => setContentForm({ ...contentForm, config: { ...contentForm.config, empresa: e.target.value } })} placeholder="QAsesores S.R.L." className="h-8 text-xs" /></div>
                  <div>
                    <Label className="text-xs">Tamaño letra</Label>
                    <select value={contentForm.config?.fontSize || "normal"} onChange={e => setContentForm({ ...contentForm, config: { ...contentForm.config, fontSize: e.target.value } })} className="h-8 text-xs w-full border border-slate-300 rounded px-2 bg-white">
                      <option value="small">Pequeño</option>
                      <option value="normal">Normal</option>
                      <option value="large">Grande</option>
                      <option value="xlarge">Muy Grande</option>
                    </select>
                  </div>
                </div>
              )}
              {contentForm.tipo === "sala" && (
                <>
                  <div><Label>Evento/Reunión actual</Label><Input value={contentForm.texto} onChange={e => setContentForm({ ...contentForm, texto: e.target.value })} placeholder="Ej: Reunión de directivos" /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="text-xs">Ubicación</Label><Input value={contentForm.config?.ubicacion || ""} onChange={e => setContentForm({ ...contentForm, config: { ...contentForm.config, ubicacion: e.target.value } })} placeholder="Piso 2" className="h-8 text-xs" /></div>
                    <div><Label className="text-xs">Ocupada hasta</Label><Input type="time" value={contentForm.config?.hasta || ""} onChange={e => setContentForm({ ...contentForm, config: { ...contentForm.config, hasta: e.target.value } })} className="h-8 text-xs" /></div>
                  </div>
                </>
              )}
              {(contentForm.tipo === "texto" || contentForm.tipo === "bienvenida") && (
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-xs">Color fondo</Label><Input type="color" value={contentForm.config?.bgColor || "#1e293b"} onChange={e => setContentForm({ ...contentForm, config: { ...contentForm.config, bgColor: e.target.value } })} className="h-8" /></div>
                  <div><Label className="text-xs">Color texto</Label><Input type="color" value={contentForm.config?.textColor || "#ffffff"} onChange={e => setContentForm({ ...contentForm, config: { ...contentForm.config, textColor: e.target.value } })} className="h-8" /></div>
                </div>
              )}
              <div>
                <Label>Duración</Label>
                {contentForm.tipo === "video" && (
                  <label className="flex items-center gap-2 mb-2 cursor-pointer">
                    <input type="checkbox" checked={contentForm.duracion === 0} onChange={(e) => setContentForm({ ...contentForm, duracion: e.target.checked ? 0 : 30 })} className="h-3.5 w-3.5 rounded" />
                    <span className="text-xs text-slate-600">Usar duración completa del video</span>
                  </label>
                )}
                {(contentForm.tipo !== "video" || contentForm.duracion !== 0) && (
                  <Input type="number" min={3} max={3600} value={contentForm.duracion || ""} onChange={e => setContentForm({ ...contentForm, duracion: parseInt(e.target.value) || 0 })} placeholder="Segundos" />
                )}
                {contentForm.tipo === "video" && contentForm.duracion === 0 && (
                  <p className="text-[11px] text-green-600 mt-1">El video se reproducirá completo antes de pasar al siguiente</p>
                )}
              </div>
              <div className="border-t pt-3">
                <Label className="text-xs font-medium text-slate-600">Programar horario (opcional)</Label>
                <p className="text-[11px] text-slate-500 mb-2">Solo mostrar este contenido en un horario específico</p>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-[11px]">Desde</Label><Input type="time" value={contentForm.config?.horario_inicio || ""} onChange={e => setContentForm({ ...contentForm, config: { ...contentForm.config, horario_inicio: e.target.value } })} className="h-8 text-xs" /></div>
                  <div><Label className="text-[11px]">Hasta</Label><Input type="time" value={contentForm.config?.horario_fin || ""} onChange={e => setContentForm({ ...contentForm, config: { ...contentForm.config, horario_fin: e.target.value } })} className="h-8 text-xs" /></div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowContentDialog(false)}>Cancelar</Button>
              <Button onClick={handleSaveContent}>Guardar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog: Play Options */}
        <Dialog open={showPlayOption} onOpenChange={(open) => { if (!open) { setShowPlayOption(false); setPendingContent(null); fetchContenido(selectedCanal?.id); } }}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader><DialogTitle>Contenido agregado</DialogTitle></DialogHeader>
            <p className="text-sm text-slate-600 mb-1">"{pendingContent?.titulo || pendingContent?.texto?.slice(0, 30) || 'Nuevo contenido'}" fue agregado. ¿Cuándo deseas reproducirlo?</p>
            <div className="space-y-2 mt-3">
              <button onClick={() => handlePlayOption("now")} className="w-full flex items-center gap-3 p-3 rounded-lg border border-blue-200 bg-blue-50 hover:bg-blue-100 transition-colors text-left">
                <Play className="w-5 h-5 text-blue-600 shrink-0" />
                <div><p className="text-sm font-medium text-blue-900">Reproducir ahora</p><p className="text-[11px] text-blue-600">Lo pone de primero y se muestra de inmediato</p></div>
              </button>
              <button onClick={() => handlePlayOption("next")} className="w-full flex items-center gap-3 p-3 rounded-lg border border-amber-200 bg-amber-50 hover:bg-amber-100 transition-colors text-left">
                <ChevronDown className="w-5 h-5 text-amber-600 shrink-0" />
                <div><p className="text-sm font-medium text-amber-900">Después del actual</p><p className="text-[11px] text-amber-600">Se reproduce justo después del contenido en pantalla</p></div>
              </button>
              <button onClick={() => handlePlayOption("last")} className="w-full flex items-center gap-3 p-3 rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100 transition-colors text-left">
                <ListOrdered className="w-5 h-5 text-slate-600 shrink-0" />
                <div><p className="text-sm font-medium text-slate-900">Al final de la cola</p><p className="text-[11px] text-slate-600">Se reproducirá al terminar todos los demás</p></div>
              </button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Dialog: QR Code del canal */}
        <Dialog open={!!qrCanal} onOpenChange={(open) => !open && setQrCanal(null)}>
          <DialogContent className="sm:max-w-md" data-testid="qr-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <QrCode className="w-5 h-5 text-purple-600" />
                Código QR — {qrCanal?.nombre}
              </DialogTitle>
            </DialogHeader>
            {qrCanal && (
              <div className="flex flex-col items-center space-y-4 py-2">
                <div className="p-5 bg-white rounded-xl border-2 border-slate-200 shadow-sm">
                  <QRCodeCanvas
                    id={`qr-canvas-${qrCanal.id}`}
                    value={getCanalPublicUrl(qrCanal)}
                    size={280}
                    level="H"
                    includeMargin={false}
                    marginSize={2}
                  />
                </div>
                <div className="w-full text-center space-y-1">
                  <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">URL del display</p>
                  <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 rounded px-2 py-1.5">
                    <Link className="w-3 h-3 text-slate-500 shrink-0" />
                    <span className="text-xs font-mono truncate flex-1 text-left" data-testid="qr-url-text">
                      {getCanalPublicUrl(qrCanal)}
                    </span>
                    <button
                      onClick={() => copyDisplayUrl(qrCanal)}
                      className="p-1 rounded hover:bg-slate-200"
                      title="Copiar URL"
                    >
                      <Copy className="w-3 h-3 text-blue-500" />
                    </button>
                  </div>
                </div>
                <p className="text-[11px] text-slate-500 text-center max-w-xs">
                  Escanea este código con la cámara de un teléfono para abrir el display. Ideal para pegarlo cerca de cada TV.
                </p>
              </div>
            )}
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button variant="outline" onClick={() => setQrCanal(null)} className="sm:flex-1">
                Cerrar
              </Button>
              <Button onClick={() => qrCanal && downloadQrCode(qrCanal)} className="sm:flex-1" data-testid="qr-download-btn">
                <Download className="w-4 h-4 mr-2" />
                Descargar PNG
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
